import { useEffect, useState } from "react";

// ── Dictator Player Controller ────────────────────────────────────────────────

const ACCENT = "#FFD700";

function Timer({ seconds }) {
  const isLow = seconds <= 10;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: "3rem", height: "3rem", borderRadius: "50%",
      border: `3px solid ${isLow ? "#ff3355" : ACCENT}`,
      boxShadow: `0 0 12px ${isLow ? "#ff335588" : "#FFD70066"}`,
      transition: "border-color 0.3s, box-shadow 0.3s",
    }}>
      <span className="font-display" style={{ color: isLow ? "#ff3355" : ACCENT, fontSize:"var(--fs-xl)" }}>
        {seconds}
      </span>
    </div>
  );
}

export default function DictatorPlayer({
  phase, data, law, setLaw, voteData, result, voted, setVoted,
  onSubmitLaw, onVote, onEndDiscussion,
}) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [prevPhase, setPrevPhase] = useState(null);

  // Sync timer to phase
  useEffect(() => {
    if (phase === prevPhase) return;
    setPrevPhase(phase);
    if (phase === "discussion") setTimeLeft(60);
    else if (phase === "vote") setTimeLeft(30);
    else setTimeLeft(null);
  }, [phase]); // eslint-disable-line

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  // ── Result ────────────────────────────────────────────────────────────────
  if (phase === "result" && result) {
    const me = sessionStorage.getItem("playerName");
    const myVote = result.votes?.[me];
    const isDictator = result.dictator === me;
    const correct = myVote && myVote === result.most_accused;
    const consensus = result.most_accused &&
      Object.values(result.votes || {}).filter(v => v === result.most_accused).length >
      Object.keys(result.votes || {}).length / 2;
    const myPts = result.scores?.[me] || 0;

    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-slide-up">
        <div style={{ fontSize:"var(--fs-3xl)" }}>
          {isDictator && consensus ? "👑" : isDictator ? "😤" : correct ? "🎯" : "😅"}
        </div>
        <div className="font-display text-center" style={{
          fontSize:"var(--fs-xl)",
          color: (isDictator && consensus) || correct ? "#3dffa0" : "#ff8c00",
        }}>
          {isDictator && consensus ? "Нарушитель пойман!"
            : isDictator ? "Не поймали..."
            : correct ? "Верно угадал!"
            : "Мимо!"}
        </div>

        {myVote && !isDictator && (
          <div className="font-heading text-[#aaa] text-center" style={{ fontSize:"var(--fs-sm)" }}>
            Ты обвинил: <span className="text-white">{myVote}</span>
          </div>
        )}

        <div style={{
          background: "#FFD70022", border: `1.5px solid ${ACCENT}`,
          borderRadius: "1rem", padding: "0.7rem 1.2rem", width: "100%", textAlign: "center",
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-xs)" }}>Главный нарушитель</div>
          <div className="font-display" style={{ color: ACCENT, fontSize:"var(--fs-xl)", marginTop: "0.2rem" }}>
            {result.most_accused || "Не определён"}
          </div>
          {consensus && (
            <div className="font-heading text-[#3dffa0]" style={{ fontSize:"var(--fs-xs)" }}>
              ✅ Консенсус достигнут
            </div>
          )}
        </div>

        <div style={{
          background: "#1a1030", border: "2px solid #3dffa055",
          borderRadius: "1rem", padding: "0.6rem 1.5rem", textAlign: "center",
        }}>
          <div className="font-heading text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>Твои очки</div>
          <div className="font-display text-[#3dffa0]" style={{ fontSize:"var(--fs-2xl)" }}>{myPts}</div>
        </div>
        <div className="font-heading text-[#555] text-center" style={{ fontSize:"var(--fs-sm)" }}>
          Следующий раунд...
        </div>
      </div>
    );
  }

  // ── Voting ────────────────────────────────────────────────────────────────
  if (phase === "vote" && voteData) {
    const candidates = (voteData.players || []).filter(p => p !== voteData.dictator);
    const me = sessionStorage.getItem("playerName");
    const isDictator = voteData.dictator === me;

    if (isDictator) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-slide-up">
          <div style={{ fontSize:"var(--fs-3xl)" }}>👑</div>
          <div className="font-display text-center" style={{ fontSize:"var(--fs-xl)", color: ACCENT }}>Ты диктатор!</div>
          <div className="font-heading text-[#aaa] text-center" style={{ fontSize:"var(--fs-md)", lineHeight: 1.5 }}>
            Игроки голосуют кто нарушил твой закон...
          </div>
          <div style={{
            background: "#FFD70022", border: `1.5px solid ${ACCENT}`,
            borderRadius: "0.75rem", padding: "0.6rem 1rem", textAlign: "center", width: "100%",
          }}>
            <div className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-xs)" }}>Твой закон</div>
            <div className="font-heading text-white" style={{ fontSize:"var(--fs-md)", lineHeight: 1.4 }}>
              {voteData.law}
            </div>
          </div>
          {timeLeft !== null && <Timer seconds={timeLeft} />}
        </div>
      );
    }

    return (
      <div className="flex-1 flex flex-col gap-3 animate-slide-up">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="font-display" style={{ fontSize:"var(--fs-xl)", color: ACCENT }}>
            🗳️ Кто нарушил?
          </div>
          {timeLeft !== null && <Timer seconds={timeLeft} />}
        </div>

        <div style={{
          background: "#FFD70022", border: `1.5px solid ${ACCENT}`,
          borderRadius: "0.75rem", padding: "0.6rem 1rem",
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-xs)" }}>Закон</div>
          <div className="font-heading text-white" style={{ fontSize:"var(--fs-md)", lineHeight: 1.4 }}>
            {voteData.law}
          </div>
        </div>

        {voted ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="font-heading text-[#3dffa0] text-center" style={{ fontSize:"var(--fs-md)" }}>
              ✅ Голос принят!<br/>
              <span style={{ color: "#555", fontSize:"var(--fs-sm)" }}>Ждём остальных...</span>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap:"var(--gap-md)", flex: 1 }}>
            <div className="font-heading text-[#aaa] text-center" style={{ fontSize:"var(--fs-xs)" }}>
              Выбери нарушителя (за себя нельзя голосовать)
            </div>
            {candidates.filter(p => p !== me).map(p => (
              <button key={p} onClick={() => { setVoted(true); onVote(p); }} style={{
                background: "#ffffff0a", border: "2px solid #ffffff22",
                borderRadius: "0.75rem", padding:"var(--pad-md)",
                color: "#fff", cursor: "pointer", width: "100%",
                WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
              }}>
                <span className="font-heading" style={{ fontSize:"var(--fs-lg)" }}>👤 {p}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Discussion ────────────────────────────────────────────────────────────
  if (phase === "discussion") {
    const me = sessionStorage.getItem("playerName");
    const isDictator = data?.dictator === me;
    const currentLaw = data?.law || data?.dictator_law || "";

    return (
      <div className="flex-1 flex flex-col gap-3 animate-slide-up">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="font-display" style={{ fontSize:"var(--fs-lg)", color: ACCENT }}>
            {isDictator ? "👑 Ты диктатор!" : "⚠️ Соблюдай закон!"}
          </div>
          {timeLeft !== null && <Timer seconds={timeLeft} />}
        </div>

        <div style={{
          background: "#FFD70033", border: `2px solid ${ACCENT}`,
          borderRadius: "1rem", padding:"var(--pad-md) var(--pad-lg)",
          boxShadow: `0 0 16px ${ACCENT}22`,
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-xs)", marginBottom: "0.25rem" }}>⚖️ ЗАКОН</div>
          <div className="font-heading text-white" style={{ fontSize:"var(--fs-lg)", lineHeight: 1.4 }}>
            {currentLaw}
          </div>
        </div>

        <div style={{
          background: "#111", border: "1.5px solid #333",
          borderRadius: "0.75rem", padding: "0.6rem 1rem",
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-xs)", marginBottom: "0.2rem" }}>📋 ТЕМА РАЗГОВОРА</div>
          <div className="font-heading text-white" style={{ fontSize:"var(--fs-md)", lineHeight: 1.4 }}>
            {data?.task}
          </div>
        </div>

        {isDictator ? (
          <>
            <div style={{
              background: "#0a0a20", border: "1px solid #ffffff10",
              borderRadius: "0.75rem", padding:"var(--pad-sm) var(--pad-md)", textAlign: "center",
            }}>
              <span className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-xs)" }}>
                Следи чтобы никто не нарушал закон. Когда закончите разговор — нажми кнопку.
              </span>
            </div>
            <button onClick={onEndDiscussion} style={{
              background: "#ff3355", border: "2.5px solid #000",
              borderRadius: "0.75rem", padding:"var(--pad-md)",
              color: "#fff", cursor: "pointer", width: "100%",
              boxShadow: "3px 3px 0 #000",
              WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
            }}>
              <span className="font-heading" style={{ fontSize:"var(--fs-lg)" }}>⏭ Завершить обсуждение</span>
            </button>
          </>
        ) : (
          <div style={{
            background: "#1a0010", border: "1.5px solid #ff335533",
            borderRadius: "0.75rem", padding:"var(--pad-sm) var(--pad-md)", textAlign: "center",
          }}>
            <span className="font-heading text-[#ff3355]" style={{ fontSize:"var(--fs-sm)" }}>
              ❗ Не нарушай закон — иначе тебя обвинят!
            </span>
          </div>
        )}
      </div>
    );
  }

  // ── Dictator sets the law ─────────────────────────────────────────────────
  if (phase === "make_law") {
    const canSubmit = law.trim().length >= 3;
    return (
      <div className="flex-1 flex flex-col gap-3 animate-slide-up">
        <div className="text-center" style={{ fontSize:"var(--fs-3xl)" }}>👑</div>
        <div className="font-display text-center" style={{ fontSize:"var(--fs-xl)", color: ACCENT }}>
          Ты диктатор!
        </div>

        <div style={{
          background: "#111", border: "1.5px solid #333",
          borderRadius: "0.75rem", padding:"var(--pad-sm) var(--pad-md)",
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-xs)", marginBottom: "0.25rem" }}>ТЕМА РАЗГОВОРА</div>
          <div className="font-heading text-white" style={{ fontSize:"var(--fs-md)", lineHeight: 1.4 }}>
            {data?.task}
          </div>
        </div>

        <div className="font-heading text-[#aaa] text-center" style={{ fontSize:"var(--fs-sm)", lineHeight: 1.5 }}>
          Придумай закон — остальные обязаны его соблюдать во время разговора по теме выше. Нарушитель будет выбран голосованием!
        </div>

        <div style={{ width: "100%" }}>
          <textarea
            value={law}
            onChange={e => setLaw(e.target.value)}
            placeholder="Например: нельзя называть числа..."
            maxLength={200}
            style={{
              width: "100%", minHeight: "90px",
              background: "#1a1a2e", border: `2px solid ${ACCENT}`,
              borderRadius: "0.75rem", padding: "0.8rem",
              color: "#fff", fontSize:"var(--fs-md)", resize: "none",
              boxSizing: "border-box", outline: "none",
              fontFamily: "var(--font-body)",
            }}
          />
          <div className="font-heading text-[#555] text-right" style={{ fontSize:"var(--fs-xs)", marginTop: "0.15rem" }}>
            {law.length}/200
          </div>
        </div>

        <button onClick={onSubmitLaw} disabled={!canSubmit} style={{
          background: canSubmit ? ACCENT : "#333",
          border: "2.5px solid #000", borderRadius: "0.75rem",
          padding: "0.85rem 2rem",
          color: canSubmit ? "#000" : "#555",
          cursor: canSubmit ? "pointer" : "not-allowed",
          boxShadow: canSubmit ? "3px 3px 0 #000" : "none",
          width: "100%", transition: "all 0.15s",
          WebkitTapHighlightColor: "transparent",
        }}>
          <span className="font-heading" style={{ fontSize:"var(--fs-lg)" }}>⚡ Объявить закон!</span>
        </button>
      </div>
    );
  }

  // ── Waiting (another player is dictator) ──────────────────────────────────
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4">
      <div style={{ fontSize:"var(--fs-3xl)" }}>⏳</div>
      <div className="font-display text-center" style={{ fontSize:"var(--fs-xl)", color: ACCENT }}>
        {data?.dictator} придумывает закон...
      </div>
      <div style={{
        background: "#0a0a20", border: "1px solid #ffffff10",
        borderRadius: "0.75rem", padding: "0.6rem 1.2rem", textAlign: "center",
      }}>
        <div className="font-heading text-[#444]" style={{ fontSize:"var(--fs-xs)", lineHeight: 1.6 }}>
          Скоро вы получите закон и тему разговора.<br/>
          Следите — кто нарушит закон, тот и станет обвиняемым!
        </div>
      </div>
      <div className="font-heading text-[#555] text-center" style={{ fontSize:"var(--fs-sm)" }}>
        Раунд {data?.round}/{data?.max_rounds}
      </div>
    </div>
  );
}